import apiClient from './axios';
import { ApiResponse, PaginatedResponse } from '../types';

export type ReferralStatus = 'pending' | 'completed';

export interface ReferralUserSummary {
  id: string;
  name: string;
  email: string | null;
  phoneNumber: string | null;
}

export interface AdminReferral {
  id: string;
  referralCode: string;
  status: ReferralStatus;
  rewardAmount: number;
  rewardCurrency: string;
  rewardLabel: string;
  referrer: ReferralUserSummary | null;
  referredUser: ReferralUserSummary | null;
  /** A business invited to PayIQ Business, rather than a person. */
  referredBusiness?: { id: string; name: string } | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReferralLeaderboardEntry {
  rank: number;
  referrerId: string;
  referrerName: string;
  referrerEmail: string | null;
  referralCode: string;
  totalReferrals: number;
  completedReferrals: number;
  pendingReferrals: number;
  totalEarned: number;
  totalEarnedLabel: string;
  lastReferralAt: string | null;
}

export interface ReferralLeaderboard {
  stats: {
    totalReferrals: number;
    completedReferrals: number;
    pendingReferrals: number;
    totalEarned: number;
    totalEarnedLabel: string;
    referrers: number;
  };
  leaderboard: ReferralLeaderboardEntry[];
}

export type QualifyingTier = 'tier_1' | 'tier_2' | 'tier_3';
export type QualifyingChannel = 'transfer' | 'bill' | 'airtime';

export interface RewardSettings {
  /** Naira earned per successful referral. */
  rewardAmount: number;
  /** Naira a user must have accrued before they can withdraw. */
  minimumWithdrawal: number;
  /** Smallest payment by an invited user that earns the referrer a reward. */
  qualifyingAmount: number;
  /** Lowest identity tier an invited user must reach. Never 'tier_0' — the
   *  verification gate can be raised but not removed. */
  qualifyingTier: QualifyingTier;
  /** Which kinds of payment count. Never empty. */
  qualifyingChannels: QualifyingChannel[];
  /** Whether the invitee must also make a qualifying payment. Off, verifying
   *  is enough. Absent from an older backend, which always requires one. */
  requiresPayment?: boolean;
  /** Naira for a business that signs up with someone's code and qualifies.
   *  0 is off. Absent from an older backend, which has no business reward. */
  businessRewardAmount?: number;
}

export type ReferralPayoutStatus = 'pending' | 'paid' | 'failed';

export interface ReferralPayout {
  id: string;
  reference: string;
  amount: number;
  currency: string;
  referralCount: number;
  status: ReferralPayoutStatus;
  failureReason: string | null;
  paidAt: string | null;
  createdAt: string;
  user: { id: string; name: string; email: string | null } | null;
}

export interface ReferralPayoutsResponse
  extends PaginatedResponse<ReferralPayout> {
  summary: { totalPaid: number };
}

export interface GetReferralsParams {
  search?: string;
  status?: ReferralStatus;
  page?: number;
  limit?: number;
}

export const referralsApi = {
  getLeaderboard: async () => {
    const { data } = await apiClient.get<ApiResponse<ReferralLeaderboard>>(
      '/admin/referrals/leaderboard',
    );
    return data.data;
  },

  getReferrals: async (params: GetReferralsParams = {}) => {
    const { data } = await apiClient.get<
      ApiResponse<PaginatedResponse<AdminReferral>>
    >('/admin/referrals', { params });
    return data.data;
  },

  getSettings: async () => {
    const { data } = await apiClient.get<ApiResponse<RewardSettings>>(
      '/admin/referrals/settings',
    );
    return data.data;
  },

  updateSettings: async (patch: Partial<RewardSettings>) => {
    const { data } = await apiClient.put<ApiResponse<RewardSettings>>(
      '/admin/referrals/settings',
      patch,
    );
    return data.data;
  },

  getPayouts: async (params: { page?: number; limit?: number } = {}) => {
    const { data } = await apiClient.get<ApiResponse<ReferralPayoutsResponse>>(
      '/admin/referrals/payouts',
      { params },
    );
    return data.data;
  },

  updateStatus: async (id: string, status: ReferralStatus) => {
    const { data } = await apiClient.patch<ApiResponse<AdminReferral>>(
      `/admin/referrals/${id}/status`,
      { status },
    );
    return data.data;
  },
};
